import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAdminListMembers, useAdminReinviteMembers, useAdminToggleActiveStatus, useAdminUpdateAdminStatus } from '@/hooks';
import { cn } from '@/lib/utils';
import { useMemberStore } from '@/store/memberStore';
import { formatDate } from "@/utils/dateUtils";
import { FileDownIcon, MailIcon, RefreshCwIcon, ShieldIcon, ShieldOffIcon, UserIcon, UserX2Icon } from 'lucide-react';
import { toast } from 'sonner';

// Helper function to ensure selectedMemberIds is always an array
const ensureArray = (value: any): number[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return [];
};

interface HubAdminMemberActionsProps {
  setAdminErrorMessage: (message: string) => void;
}

export function HubAdminMemberActions({ setAdminErrorMessage }: HubAdminMemberActionsProps) {
  // Backend operations - React Query
  const { data: membersData, isLoading, refetch: refetchMembers } = useAdminListMembers();
  const members = membersData?.members || [];
  const fetchMembers = () => refetchMembers();
  const toggleAdminStatusMutation = useAdminUpdateAdminStatus();
  const toggleActiveStatusMutation = useAdminToggleActiveStatus();
  const reinviteMembersMutation = useAdminReinviteMembers();

  // UI state - Zustand
  const { 
    selectedMemberIds, 
    selectAllMembers, 
    deselectAllMembers, 
    areAllMembersSelected, 
    hasSelectedMembers,
  } = useMemberStore();

  // Ensure selectedMemberIds is an array
  const safeSelectedMemberIds = ensureArray(selectedMemberIds);

  // Determine if we're setting to admin or non-admin (based on majority)
  const selectedMembers = members.filter(emp => safeSelectedMemberIds.includes(emp.id));
  const adminCount = selectedMembers.filter(emp => emp.isAdmin).length;
  const setToAdmin = adminCount < selectedMembers.length / 2;

  // Determine if we're setting to active or inactive (based on majority)
  const activeCount = selectedMembers.filter(emp => emp.isActive).length;
  const setToActive = activeCount < selectedMembers.length / 2;

  // Check if this action would affect all admins
  const allAdmins = members.filter(emp => emp.isAdmin);
  const allAdminsSelected = allAdmins.length > 0 && 
    allAdmins.every(admin => safeSelectedMemberIds.includes(admin.id));
  const wouldRemoveAllAdmins = !setToAdmin && allAdminsSelected;

  // Check if trying to grant admin to inactive members
  const selectedInactiveMembers = selectedMembers.filter(emp => !emp.isActive);
  const wouldGrantAdminToInactive = setToAdmin && selectedInactiveMembers.length > 0;

  // Check if deactivating would remove all active admins
  const selectedActiveAdmins = selectedMembers.filter(emp => emp.isAdmin && emp.isActive);
  const allActiveAdmins = members.filter(emp => emp.isAdmin && emp.isActive);
  const wouldRemoveAllActiveAdmins = !setToActive && selectedActiveAdmins.length > 0 && 
    selectedActiveAdmins.length === allActiveAdmins.length;

  // Check for unverified members that can be reinvited
  const selectedUnverifiedMembers = selectedMembers.filter(emp => emp.accountStatus === 'UNVERIFIED');
  const selectedVerifiedMembers = selectedMembers.filter(emp => emp.accountStatus === 'VERIFIED');
  const hasUnverifiedMembers = selectedUnverifiedMembers.length > 0;
  const hasMixedSelection = selectedUnverifiedMembers.length > 0 && selectedVerifiedMembers.length > 0;

  const handleSelectAll = () => {
    if (areAllMembersSelected(members.length)) {
      deselectAllMembers();
    } else {
      selectAllMembers(members.map(emp => emp.id));
    }
  };

  const handleToggleAdminStatus = async () => {
    // Clear any existing error message
    setAdminErrorMessage('');
    
    try {
      await toggleAdminStatusMutation.mutateAsync({
        memberIds: safeSelectedMemberIds,
        isAdmin: setToAdmin
      });
      // Clear selections after successful operation
      deselectAllMembers();
    } catch (error: any) {
      if (error.message && error.message.includes('Cannot remove admin status from all admins')) {
        setAdminErrorMessage('Cannot remove admin status from all admins. At least one admin must remain.');
      } else if (error.message && error.message.includes('Cannot grant admin privileges to inactive members')) {
        setAdminErrorMessage('Cannot grant admin privileges to inactive members. Please activate the members first.');
      } else {
        setAdminErrorMessage(error.message || 'Error updating admin status');
      }
    }
  };

  const handleToggleActiveStatus = async () => {
    // Clear any existing error message
    setAdminErrorMessage('');
    
    try {
      await toggleActiveStatusMutation.mutateAsync({
        memberIds: safeSelectedMemberIds,
        isActive: setToActive
      });
      toast.success(`Successfully ${setToActive ? 'activated' : 'deactivated'} selected members`);
      // Clear selections after successful operation
      deselectAllMembers();
    } catch (error: any) {
      let errorMessage = error instanceof Error ? error.message : 'Error updating active status';
      if (error.message && error.message.includes('cannot deactivate these members as it would leave the hub without any active admins')) {
        errorMessage = 'Cannot deactivate these members as it would leave the hub without any active admins. Please ensure at least one admin remains active.';
      }
      setAdminErrorMessage(errorMessage);
    }
  };

  const handleReinviteMembers = async () => {
    // Clear any existing error message
    setAdminErrorMessage('');
    
    try {
      if (!hasUnverifiedMembers) {
        toast.error('Please select unverified members to reinvite');
        return;
      }
      
      // Show warning for mixed selections
      if (hasMixedSelection) {
        toast.warning(
          `Only ${selectedUnverifiedMembers.length} unverified member${selectedUnverifiedMembers.length === 1 ? '' : 's'} will be reinvited. ${selectedVerifiedMembers.length} verified member${selectedVerifiedMembers.length === 1 ? '' : 's'} will be skipped.`, 
          { duration: 4000 }
        );
      }
      
      // Only reinvite unverified members
      const unverifiedMemberIds = selectedUnverifiedMembers.map(emp => emp.id);
      await reinviteMembersMutation.mutateAsync({
        memberIds: unverifiedMemberIds
      });
      
      // Provide detailed success feedback
      if (hasMixedSelection) {
        toast.success(
          `Successfully reinvited ${selectedUnverifiedMembers.length} unverified member${selectedUnverifiedMembers.length === 1 ? '' : 's'}. ${selectedVerifiedMembers.length} verified member${selectedVerifiedMembers.length === 1 ? ' was' : 's were'} skipped.`,
          { duration: 4000 }
        );
      } else {
        toast.success(
          `Successfully reinvited ${selectedUnverifiedMembers.length} member${selectedUnverifiedMembers.length === 1 ? '' : 's'}.`
        );
      }
      
      // Clear selections after successful operation
      deselectAllMembers();
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Error sending reinvitations';
      setAdminErrorMessage(errorMessage);
    }
  };

  const handleExportToCsv = () => {
    // Clear any existing error message
    setAdminErrorMessage('');
    
    try {
      if (!hasSelectedMembers()) {
        toast.error('Please select at least one member to export');
        return;
      }
      
      // Get selected members
      const membersToExport = members.filter(emp => safeSelectedMemberIds.includes(emp.id));
      
      // Create CSV content
      const headers = ['ID', 'Email', 'First Name', 'Surname', 'Status', 'Role', 'Active', 'Created At'];
      const csvContent = membersToExport.map(emp => [
        emp.id,
        emp.email,
        emp.firstName || '',
        emp.surname || '',
        emp.accountStatus || '',
        emp.isAdmin ? 'Admin' : 'User',
        emp.isActive ? 'Active' : 'Inactive',
        formatDate(new Date(emp.createdAt))
      ]);
      
      // Convert to CSV string
      const csvString = [
        headers.join(','),
        ...csvContent.map(row => row.join(','))
      ].join('\n');
      
      // Create a blob and download link
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `members_export_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Members exported successfully');
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Error exporting members';
      setAdminErrorMessage(errorMessage);
    }
  };

  return (
    <TooltipProvider>
      <div className="pb-2 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer">
                  <Checkbox
                    className="mr-2"
                    checked={areAllMembersSelected(members.length)}
                    onCheckedChange={handleSelectAll}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{areAllMembersSelected(members.length) ? 'Deselect all members' : 'Select all members'}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'h-8 w-8',
                    isLoading ? 'cursor-wait' : 'cursor-pointer'
                  )}
                  disabled={isLoading}
                  onClick={fetchMembers}
                >
                  <RefreshCwIcon className={cn('h-4 w-4', isLoading ? 'animate-spin' : '')} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isLoading ? 'Refreshing member list...' : 'Refresh member list'}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(
                  !hasSelectedMembers() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                  isLoading ? 'cursor-wait opacity-50' : '',
                  wouldRemoveAllAdmins || wouldGrantAdminToInactive ? 'text-destructive' : ''
                )}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleToggleAdminStatus}
                    disabled={!hasSelectedMembers() || isLoading || wouldGrantAdminToInactive}
                  >
                    {setToAdmin ? (
                      <ShieldIcon className="h-4 w-4" />
                    ) : (
                      <ShieldOffIcon className="h-4 w-4" />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {!hasSelectedMembers() 
                    ? 'Select members to toggle admin status' 
                    : wouldRemoveAllAdmins 
                      ? '⚠️ This will remove admin status from all admins (not allowed)'
                      : wouldGrantAdminToInactive
                        ? '⚠️ Cannot grant admin privileges to inactive members (activate them first)'
                        : `${setToAdmin ? 'Grant' : 'Remove'} admin privileges for selected members`
                  }
                </p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(
                  !hasSelectedMembers() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                  isLoading ? 'cursor-wait opacity-50' : '',
                  wouldRemoveAllActiveAdmins ? 'text-destructive' : ''
                )}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleToggleActiveStatus}
                    disabled={!hasSelectedMembers() || isLoading}
                  >
                    {setToActive ? (
                      <UserIcon className="h-4 w-4" />
                    ) : (
                      <UserX2Icon className="h-4 w-4" />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {!hasSelectedMembers() 
                    ? 'Select members to toggle active status' 
                    : wouldRemoveAllActiveAdmins
                      ? '⚠️ This will deactivate all active admins and revoke their admin privileges'
                      : setToActive 
                        ? 'Activate selected members'
                        : `Deactivate selected members${selectedActiveAdmins.length > 0 ? ' (will also revoke admin privileges)' : ''}`
                  }
                </p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(
                  !hasUnverifiedMembers ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                  isLoading ? 'cursor-wait opacity-50' : ''
                )}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleReinviteMembers}
                    disabled={!hasUnverifiedMembers || isLoading}
                  >
                    <MailIcon className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {!hasSelectedMembers() 
                    ? 'Select unverified members to resend invitations' 
                    : !hasUnverifiedMembers
                      ? 'Only unverified members can be reinvited'
                      : hasMixedSelection
                        ? `Will reinvite ${selectedUnverifiedMembers.length} unverified member${selectedUnverifiedMembers.length === 1 ? '' : 's'} and skip ${selectedVerifiedMembers.length} verified member${selectedVerifiedMembers.length === 1 ? '' : 's'}`
                        : `Resend invitation${selectedUnverifiedMembers.length === 1 ? '' : 's'} to ${selectedUnverifiedMembers.length} unverified member${selectedUnverifiedMembers.length === 1 ? '' : 's'}`
                  }
                </p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(
                  !hasSelectedMembers() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                  isLoading ? 'cursor-wait opacity-50' : ''
                )}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleExportToCsv}
                    disabled={!hasSelectedMembers() || isLoading}
                  >
                    <FileDownIcon className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {!hasSelectedMembers() 
                    ? 'Select members to export to CSV' 
                    : `Export ${safeSelectedMemberIds.length} selected member${safeSelectedMemberIds.length === 1 ? '' : 's'} to CSV`
                  }
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="text-sm text-muted-foreground">
            {safeSelectedMemberIds.length > 0 && (
              <span>{safeSelectedMemberIds.length} of {members.length} selected</span>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
} 